import React, { useEffect, useRef } from "react";

// Real WHEP playback over native browser WebRTC.
//
// Flow (for the WHEP endpoint /<id>/webRTC/play):
//   1. RTCPeerConnection with recvonly audio+video transceivers
//   2. createOffer -> setLocalDescription -> wait for ICE gathering
//   3. POST the SDP offer to the WHEP URL (Content-Type: application/sdp)
//   4. setRemoteDescription(answer) -> remote track attaches to <video>
//
// No WHIP/publish credential is ever involved here — this is the public,
// playback-only side. No iframe, no user JWT. Reconnects with bounded backoff;
// after exhausting retries it reports `onExhausted` so the parent can fall back.

const MAX_RETRIES = 2;
const CONNECT_TIMEOUT_MS = 8000;

function waitForIceGathering(pc, timeoutMs = 2500) {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener("icegatheringstatechange", check);
      clearTimeout(timer);
      resolve();
    };
    const check = () => {
      if (pc.iceGatheringState === "complete") done();
    };
    const timer = setTimeout(done, timeoutMs); // proceed with whatever candidates we have
    pc.addEventListener("icegatheringstatechange", check);
  });
}

export default function WebRTCPlayer({
  src,
  muted = true,
  onHealth,
  onExhausted,
  className = "",
  "data-testid": testId,
}) {
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const aliveRef = useRef(true);
  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  // Keep the latest callbacks in refs so the connection effect only depends on `src`.
  const onHealthRef = useRef(onHealth);
  const onExhaustedRef = useRef(onExhausted);
  onHealthRef.current = onHealth;
  onExhaustedRef.current = onExhausted;

  useEffect(() => {
    aliveRef.current = true;
    retryRef.current = 0;
    const health = (s) => { if (aliveRef.current && onHealthRef.current) onHealthRef.current(s); };

    let connectTimeout = null;

    const clearConnectTimeout = () => {
      if (connectTimeout) {
        clearTimeout(connectTimeout);
        connectTimeout = null;
      }
    };

    const teardownPc = () => {
      clearConnectTimeout();
      if (abortRef.current) { try { abortRef.current.abort(); } catch { /* noop */ } abortRef.current = null; }
      const pc = pcRef.current;
      pcRef.current = null;
      if (pc) {
        try { pc.getReceivers().forEach((r) => r.track && r.track.stop()); } catch { /* noop */ }
        try { pc.ontrack = null; pc.onconnectionstatechange = null; pc.oniceconnectionstatechange = null; } catch { /* noop */ }
        try { pc.close(); } catch { /* noop */ }
      }
      const v = videoRef.current;
      if (v && v.srcObject) { try { v.srcObject = null; } catch { /* noop */ } }
    };

    const scheduleReconnect = () => {
      if (!aliveRef.current) return;
      retryRef.current += 1;
      if (retryRef.current > MAX_RETRIES) {
        health("offline");
        if (typeof onExhaustedRef.current === "function") onExhaustedRef.current();
        return;
      }
      health("reconnecting");
      const delay = Math.min(4000, 1000 * 2 ** (retryRef.current - 1)); // 1s, then 2s
      timerRef.current = setTimeout(connect, delay);
    };

    async function connect() {
      if (!aliveRef.current || !src) return;
      teardownPc();
      health(retryRef.current === 0 ? "connecting" : "reconnecting");

      let pc;
      try {
        pc = new RTCPeerConnection({ bundlePolicy: "max-bundle", iceServers: [] });
        pcRef.current = pc;
        pc.addTransceiver("video", { direction: "recvonly" });
        pc.addTransceiver("audio", { direction: "recvonly" });

        // The WHEP reference flow attaches every incoming track to a
        // MediaStream explicitly. Do not rely on ev.streams[0] being populated:
        // some browsers deliver streamless tracks for recvonly transceivers.
        const remoteStream = new MediaStream();
        const v = videoRef.current;
        if (v) v.srcObject = remoteStream;
        pc.ontrack = (ev) => {
          if (!remoteStream.getTracks().some((track) => track.id === ev.track.id)) {
            remoteStream.addTrack(ev.track);
          }
          const video = videoRef.current;
          if (video) {
            const p = video.play();
            if (p && typeof p.catch === "function") p.catch(() => { /* autoplay allowed because muted */ });
          }
        };

        const failAndReconnect = () => {
          if (!aliveRef.current || pcRef.current !== pc) return;
          teardownPc();
          scheduleReconnect();
        };

        pc.onconnectionstatechange = () => {
          if (!aliveRef.current || pcRef.current !== pc) return;
          const st = pc.connectionState;
          if (st === "connected") {
            clearConnectTimeout();
            retryRef.current = 0;
            health("live");
          } else if (st === "failed" || st === "closed") {
            failAndReconnect();
          } else if (st === "disconnected") {
            health("reconnecting");
          }
        };

        pc.oniceconnectionstatechange = () => {
          if (!aliveRef.current || pcRef.current !== pc) return;
          const st = pc.iceConnectionState;
          if (st === "failed") failAndReconnect();
          else if (st === "disconnected") health("reconnecting");
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await waitForIceGathering(pc);
        if (!aliveRef.current || pcRef.current !== pc) return;

        abortRef.current = new AbortController();
        const res = await fetch(src, {
          method: "POST",
          headers: { "Content-Type": "application/sdp" },
          body: pc.localDescription.sdp,
          signal: abortRef.current.signal,
        });
        if (!aliveRef.current || pcRef.current !== pc) return;
        if (!res.ok) throw new Error(`WHEP ${res.status}`);
        const answer = await res.text();
        if (!aliveRef.current || pcRef.current !== pc) return;
        await pc.setRemoteDescription({ type: "answer", sdp: answer });

        // A WHEP POST can succeed while ICE never establishes (for example if
        // ingest/playback protocols are mismatched). Never leave the UI stuck
        // at "Connecting" forever: force a bounded retry/fallback.
        clearConnectTimeout();
        connectTimeout = setTimeout(() => {
          if (!aliveRef.current || pcRef.current !== pc) return;
          if (pc.connectionState !== "connected") failAndReconnect();
        }, CONNECT_TIMEOUT_MS);
        // pc.onconnectionstatechange -> "connected" flips health to "live"
      } catch (err) {
        if (!aliveRef.current) return;
        teardownPc();
        scheduleReconnect();
      }
    }

    connect();

    return () => {
      aliveRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      teardownPc();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted={muted}
      playsInline
      controls={false}
      className={`absolute inset-0 h-full w-full object-cover ${className}`}
      data-testid={testId || "bjv2-webrtc"}
    />
  );
}
